import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

/* eslint-disable global-require */
export type HttpMethod = 'get' | 'put' | 'post' | 'delete';

export type HttpClient = (
  url: string,
  params: Record<string, unknown>,
  body: Record<string, unknown>,
  method: HttpMethod,
  config?: { disableBaseUrl?: boolean },
) => Promise<any>;

type CustomHttpResponse<T = unknown> = {
  code: 'SUCCESS' | 'UNKNOWN_ERROR';
  data: T | null;
  msg: string;
  success: boolean;
};

export function createRequestUtil<ApiConfig>(
  httpGetter: () => HttpClient | null,
  baseUrlGetter: () => string,
  isSuccess: (data: CustomHttpResponse<any>) => boolean,
) {
  const request = <
    URL extends keyof ApiConfig,
    Config extends ApiConfig[URL],
    Method extends keyof Config,
    Args extends Config[Method] extends [infer Query, infer Body, unknown]
      ? [Query, Body]
      : never,
    Return extends Config[Method] extends [unknown, unknown, infer R]
      ? R
      : never,
    TransData = Return,
  >(
    url: URL,
    method: Method,
    options?: {
      trans?: (data: Return) => TransData;
      cache?: {
        key: string;
        idExtract: (params: Args[0], body: Args[1]) => string | null;
        expires?: number;
      };
      onOk?: (
        result: CustomHttpResponse<TransData>,
        query: { params: Args[0]; body: Args[1] },
        opt: {},
      ) => void;
      domain?: string;
    },
  ) => {
    const mockConfig = {
      mockGetter: undefined as
        | undefined
        | ((params: Args[0], body: Args[1]) => Promise<TransData>),
    };

    const fetcher = async (params: Args[0], body: Args[1]) => {
      if (mockConfig.mockGetter) {
        const data = await mockConfig.mockGetter(params, body);
        return {
          code: 'SUCCESS',
          success: true,
          data,
        } as CustomHttpResponse<TransData>;
      }

      const client = httpGetter();

      if (!client) {
        return {
          code: 'SUCCESS',
          success: true,
        } as CustomHttpResponse<TransData>;
      }

      const result = (await client(
        ((options?.domain ?? baseUrlGetter()) + (url as string)) as string,
        (params as any) ?? undefined,
        (body as any) ?? undefined,
        method as any,
      )) as any;

      if (options?.trans && result.data) {
        result.data = options.trans(result.data);
      }

      if (options?.onOk) {
        options.onOk(result, { params, body }, {});
      }
      return result as CustomHttpResponse<TransData>;
    };

    fetcher.mock = (
      mockGetter: (params: Args[0], body: Args[1]) => Promise<TransData>,
    ) => {
      mockConfig.mockGetter = mockGetter;
    };

    type ApiParams = Args[0] extends null
      ? Args[1] extends null
        ? { params?: never; body?: never }
        : { params?: never; body: Args[1] }
      : Args[1] extends null
      ? { params: Args[0]; body?: never }
      : { params: Args[0]; body: Args[1] };

    fetcher.useQuery = (
      query: ApiParams,
      options: Omit<
        UseQueryOptions<
          NonNullable<TransData>,
          unknown,
          NonNullable<TransData>,
          any[]
        >,
        'queryKey' | 'queryFn' | 'initialData'
      > & { customKeys?: any[]; initialData?: TransData },
    ) => {
      return useQuery({
        ...(options as {}),
        queryKey: [
          url,
          query.params,
          query.body,
          ...(options.customKeys ?? []),
        ],
        queryFn: async () => {
          const res = await fetcher(query.params, query.body);

          if (isSuccess(res)) {
            return res.data!;
          }

          throw new Error(res.msg);
        },
      });
    };

    fetcher.usePages = (
      query: ({ pageIndex }: { pageIndex: number }) => ApiParams,
      options: Omit<
        UseInfiniteQueryOptions<
          CustomHttpResponse<TransData>,
          unknown,
          CustomHttpResponse<TransData>,
          any[]
        >,
        'queryKey' | 'queryFn'
      > & { customKeys?: any[] },
    ) => {
      return useInfiniteQuery({
        ...(options as {}),
        queryKey: [url, ...(options.customKeys ?? [])],
        queryFn: ({ pageParam = 1 }) => {
          const { body, params } = query({ pageIndex: pageParam });
          return fetcher(params, body);
        },
      });
    };

    return fetcher;
  };

  return {
    request,
  };
}
